/**
 * Shared DTOs and Zod schemas for the documents route.
 *
 * A document is a metadata index entry for any document in your life —
 * mostly PDFs: insurance policies, permits, warranties, but equally a
 * medical bill, a receipt, a statement, a school letter. Most of these
 * never expire; they're filed to be retrieved later. So the catalogue is
 * organised on three independent facets:
 *   - WHO it's about (`pertainsTo` — people: you, spouse, a child, …)
 *   - WHAT kind it is (`category` — medical bill, insurance, receipt, …)
 *   - WHICH life area it files under (`lifeAreaId`)
 * plus optional dates (only some documents have an expiry/renewal).
 * The file itself lives in Dropbox (or wherever); the hub stores the
 * metadata and the path so "everything about Marie", "all medical bills",
 * and "what's expiring" are all fast queries. This cut is metadata-first:
 * no upload pipeline, just the record.
 *
 * The same Zod schemas are used by the API for input validation and by
 * the frontend's optimistic-update layer, so both sides agree on the
 * wire format. Mirrors DATA_MODEL.md §documents (a pragmatic subset —
 * vault-only fields like file_hash, mime_type, mirror_to_r2 land when
 * the Vault / upload work does).
 */

import { z } from 'zod';

// 'routine' = administrative paperwork with an expiry/renewal lifecycle
// (the default, bulk of the table). 'vital' = identity-defining records
// (passports, certificates, wills) — the future Vault treats these with
// extra care. 'retired' = your own old-but-keep records (an expired
// passport with stamps, a superseded permit) — out of active use but
// worth holding onto. ('retired', not 'archival', so the word "archive"
// belongs solely to Folio's family archive — see PROJECT.md §Documents
// vs Vault vs the Folio archive.) The column exists now so the Vault
// extension is a UI/behaviour change, not a migration.
export const documentCategories = ['routine', 'vital', 'retired'] as const;
export type DocumentCategory = (typeof documentCategories)[number];

// The curated document kinds — the `category` facet's standard
// vocabulary. A fixed list so the catalogue converges instead of
// accumulating near-duplicate variants ("Medical bill" / "medical
// invoice" / "Doctor's bill") from free-text and AI entry. The column
// itself stays free text: the edit drawer offers this list plus an
// "Other…" escape hatch for the rare document that doesn't fit, and
// user-added values remain selectable afterwards. The AI extractor is
// constrained to exactly this list (extractDocument.ts) so suggestions
// always land on a standard kind. Orthogonal to life areas: the kind is
// what the paper *is*, the life area is which part of life it files
// under (a paediatrician invoice = Bill / Invoice + Health).
export const documentKinds = [
  'Bill / Invoice',
  'Receipt',
  'Statement',
  'Contract',
  'Insurance',
  'Medical record',
  'Prescription',
  'Certificate',
  'Identity document',
  'Licence / Permit',
  'Tax',
  'Payslip',
  'CV / Résumé',
  'School / Education',
  'Correspondence',
  'Tickets',
  'Warranty / Manual',
  'Membership / Subscription',
  'Legal',
] as const;

// Same scope set as obligations — owner-private by default, 'shared'
// opens to the household. Kept as its own list so the documents module
// doesn't couple to the obligations module.
export const documentVisibilities = ['private', 'shared', 'family', 'public'] as const;
export type DocumentVisibility = (typeof documentVisibilities)[number];

/** ISO date `YYYY-MM-DD` or full ISO datetime — accept both. */
const isoDate = z
  .string()
  .min(8)
  .regex(/^\d{4}-\d{2}-\d{2}(T.*)?$/u, 'expected ISO date or datetime');

const baseDocumentFields = {
  id: z.string().min(8).max(64).optional(),
  title: z.string().min(1).max(200).trim(),
  // Required so every document files under a life area (Documents,
  // Health, Finances, …). The UI defaults new documents to the
  // Documents area.
  lifeAreaId: z.string().min(1),
  documentCategory: z.enum(documentCategories).default('routine'),
  // Finer free-text type within the category: 'insurance', 'permit',
  // 'passport', 'warranty', 'medical', etc. Free text (not an enum) so
  // the vocabulary can grow without a schema change; the UI offers a
  // datalist of common values.
  category: z.string().max(80).nullish(),
  // Who the document is about — a list of `people` ids (you, your
  // spouse, a child, or anyone you add). Lets the catalogue answer
  // "everything about Marie". Many people per doc (a family policy is
  // about everyone; a child's bill about one). Empty/absent = not about
  // a particular person. Distinct from `category` (what kind it is).
  pertainsTo: z.array(z.string().min(1).max(64)).max(30).nullish(),
  // Optional sub-area within the life area ("Dentist" under Health).
  // Free text reused per area via the drawer's select; adds a folder
  // segment to the Dropbox mirror path.
  subArea: z.string().max(80).nullish(),
  issuer: z.string().max(120).nullish(),
  issuedDate: isoDate.nullish(),
  // The killer query is "what's expiring". Indexed server-side.
  expiryDate: isoDate.nullish(),
  // "I've handled it" — the expiry date attention was dismissed for
  // (set by the document card's Dismiss / Add-renewal-task actions).
  // The attention surfaces go quiet while it equals expiryDate; a new
  // expiry date makes the stored value stale so reminders resume.
  expiryDismissedFor: isoDate.nullish(),
  // Where the actual file lives. Optional in this metadata-first cut —
  // you can record a document before (or without) wiring up a path.
  dropboxPath: z.string().max(1000).nullish(),
  tags: z.array(z.string().min(1).max(40)).max(20).nullish(),
  notes: z.string().max(4000).nullish(),
  // Key-document flag: one of the handful that matter for a person
  // (passport, vaccination record). Starred documents surface on the
  // person card of everyone they pertain to.
  starred: z.boolean().optional(),
  // Vault review cadence in calendar months; null/absent = the
  // 12-month default. Only meaningful on vault records.
  vaultReviewMonths: z.number().int().min(1).max(120).nullish(),
  // The trip this document belongs to, if any (bookings, tickets).
  // Null clears the link.
  tripId: z.string().min(1).max(64).nullish(),
  // Owner-private by default, enforced server-side (see the documents
  // route `canAccessDocument`). 'shared' opens it to the household.
  visibility: z.enum(documentVisibilities).default('private'),
} as const;

export const documentCreateSchema = z.object(baseDocumentFields);

export const documentUpdateSchema = z
  .object(baseDocumentFields)
  .partial()
  .extend({
    // `baseDocumentFields` bakes in create-time defaults for these two.
    // Zod's `.partial()` does NOT strip a `.default()` — an omitted
    // field still parses to its default — so on a partial update the
    // default would clobber the stored value (e.g. clearing an expiry
    // date would also reset visibility to 'private' and the category to
    // 'routine'). Override as plain optionals so "omitted" means "leave
    // unchanged". Same gotcha as obligationUpdateSchema.
    documentCategory: z.enum(documentCategories).optional(),
    visibility: z.enum(documentVisibilities).optional(),
    // Vault annual-review stamp ("Still current" sets it to now).
    // Update-only: a record can't be born pre-reviewed.
    vaultReviewedAt: z.string().datetime().nullish(),
  });

export type DocumentCreateInput = z.infer<typeof documentCreateSchema>;
export type DocumentUpdateInput = z.infer<typeof documentUpdateSchema>;

/**
 * Wire shape returned by the API. `tags` is stored as a JSON-encoded
 * array in D1 but exposed as a real array on the wire.
 */
export interface DocumentDTO {
  id: string;
  title: string;
  lifeAreaId: string;
  documentCategory: DocumentCategory;
  category: string | null;
  subArea: string | null;
  pertainsTo: string[] | null;
  // Uploaded-file metadata. `fileName`/`mimeType`/`fileSize` describe the
  // attached file; null when none is attached. The bytes are served at
  // `GET /api/almanac/documents/:id/file` (the R2 object key stays
  // server-side and is never exposed here).
  fileName: string | null;
  mimeType: string | null;
  fileSize: number | null;
  /** SHA-256 of the stored bytes — the offline sync's change signal:
   *  a replaced scan re-downloads, an untouched one never does. */
  fileHash: string | null;
  issuer: string | null;
  issuedDate: string | null;
  expiryDate: string | null;
  expiryDismissedFor: string | null;
  dropboxPath: string | null;
  /** Second Dropbox copy for Vault records (/Vault/<Kind>/…); null for
   *  routine documents. Server-managed — never in write payloads. */
  dropboxVaultPath: string | null;
  /** When the user last confirmed this vital record current (Vault
   *  annual review). Null = never reviewed. */
  vaultReviewedAt: string | null;
  /** Per-record review cadence in calendar months; null = 12. */
  vaultReviewMonths: number | null;
  tags: string[] | null;
  notes: string | null;
  starred: boolean;
  /** The trip this document belongs to, if any. */
  tripId: string | null;
  visibility: DocumentVisibility;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

// ── AI metadata extraction (Documents Phase 3) ──────────────────────
// The Worker reads an uploaded file with Claude and returns suggested
// fields for the user to confirm in the edit drawer. Everything is a
// suggestion — nothing is written until the user saves.

/** The structured fields Claude is asked to pull from a document. All
 *  optional: the model omits anything it can't find. Dates are
 *  ISO `YYYY-MM-DD`. `people` are raw names, matched to the People table
 *  server-side (see DocumentExtraction.people). */
export interface DocumentSuggestion {
  title?: string | null;
  category?: string | null;
  // A sub-area picked from the ones already in use for the suggested
  // life area — never invented. Null when none clearly fits.
  subArea?: string | null;
  issuer?: string | null;
  issuedDate?: string | null;
  expiryDate?: string | null;
  tags?: string[] | null;
  summary?: string | null;
  documentCategory?: DocumentCategory | null;
  // Which life area Claude filed the document under, resolved to one of
  // the user's existing areas server-side. Null when the model didn't
  // pick one (or its pick didn't match an area).
  lifeAreaId?: string | null;
}

/** A person Claude named in the document, resolved against the People
 *  table. `personId` is set when it matched an existing person; otherwise
 *  it's an unmatched name the user can one-tap add. */
export interface SuggestedPerson {
  name: string;
  personId: string | null;
}

/** Response of `POST /api/almanac/documents/extract`. `configured` is
 *  false when no Anthropic key is set — the client then skips straight to
 *  manual entry. `supported` is false for file types Claude can't read
 *  (e.g. HEIC). */
export interface DocumentExtraction {
  configured: boolean;
  supported: boolean;
  suggestion: DocumentSuggestion;
  people: SuggestedPerson[];
}

/* ── Metadata-derived file name ───────────────────────────────────────
   The stored file's human name — the FILE chip on the card, the
   download filename, and the basename of the Dropbox mirror — is
   derived from the record's metadata, never from what the camera
   called the upload:

     <issued date> <Title>[ — <Person>].<ext>

   One function, shared by the API (upload + metadata saves keep
   file_name in sync; the Dropbox path deriver reuses the basename)
   and the client (Settings' "tidy file names" control counts
   mismatches with the same rule). The person suffix is skipped when
   the title already names the person — "Pediatrician Invoice - Hugo
   Simmons" must not become "… - Hugo Simmons — Hugo Simmons". */

// Characters unsafe in a filename (Dropbox path component, download
// name), plus control chars. Ordinary punctuation (hyphens,
// apostrophes, commas, accents) is kept.
// eslint-disable-next-line no-control-regex
const FILENAME_ILLEGAL = /[\u0000-\u001f/\\:?*"<>|]/g;

/** Clean a string for use inside a filename or path segment. */
export function sanitizeFileSegment(raw: string, fallback: string, maxLen = 100): string {
  const cleaned = raw
    .replace(FILENAME_ILLEGAL, ' ')
    .replace(/\s+/g, ' ')
    .replace(/^[.\s]+|[.\s]+$/g, '') // no leading/trailing dots or spaces
    .trim();
  return (cleaned || fallback).slice(0, maxLen);
}

/** Extension (without dot), lowercased, from a filename; '' if none. */
export function fileExtensionOf(fileName: string): string {
  const dot = fileName.lastIndexOf('.');
  if (dot < 0 || dot === fileName.length - 1) return '';
  return fileName
    .slice(dot + 1)
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '')
    .slice(0, 10);
}

export interface FileNameInput {
  title: string;
  /** Resolved display names of the people the document is about. */
  personNames: string[];
  /** ISO issued date (YYYY-MM-DD…) if known. */
  issuedDate: string | null;
  /** ISO created timestamp — the fallback date. */
  createdAt: string;
  /** The current/original filename — used only for its extension. */
  originalFileName: string;
}

export function deriveDocumentFileName(input: FileNameInput): string {
  const dateOf = (iso: string) => /^(\d{4}-\d{2}-\d{2})/.exec(iso)?.[1] ?? '';
  const datePrefix = dateOf(input.issuedDate ?? '') || dateOf(input.createdAt);

  const titlePart = sanitizeFileSegment(input.title, 'Document', 120);
  const nameBase = [datePrefix, titlePart].filter(Boolean).join(' ');

  const people = input.personNames.map((n) => n.trim()).filter(Boolean);
  const solePerson = people.length === 1 ? sanitizeFileSegment(people[0]!, 'Person') : null;
  // Suffix only when the title doesn't already carry the name.
  const withPerson =
    solePerson && !titlePart.toLowerCase().includes(solePerson.toLowerCase())
      ? `${nameBase} — ${solePerson}`
      : nameBase;

  const ext = fileExtensionOf(input.originalFileName);
  return ext ? `${withPerson}.${ext}` : withPerson;
}

/** The 12-month default vault review cadence. */
export const DEFAULT_VAULT_REVIEW_MONTHS = 12;

/**
 * The instant a vault record's next review falls due: its anchor (the
 * last confirmation, else when it was added) plus its cadence in
 * calendar months. Shared by the client (due badges, the edit form's
 * next-due line) and the Worker cron (the review push) so the two can
 * never disagree about "due".
 */
export function vaultNextReviewIso(
  anchorIso: string,
  months: number | null | undefined,
): string {
  const d = new Date(anchorIso);
  d.setUTCMonth(d.getUTCMonth() + (months ?? DEFAULT_VAULT_REVIEW_MONTHS));
  return d.toISOString();
}
